/* 
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved. 
* Distributed under the TruVideo proprietary license. 
*/
import React, { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import logo from '../assets/logo.png';
import { usePostHog } from '@posthog/react';
import { useBranding } from '../hooks/useBranding';
import { DEFAULT_LOGIN_ROUTE, PIXO_LOGIN_ROUTE, normalizeAccountSlug, resolveLoginRoute } from '../constants/loginRoutes';

const Login = () => {
  const posthog = usePostHog();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const { login } = useAuth();
  // /login/:accountSlug — account login page; an invalid slug is treated as plain /login
  const accountSlug = normalizeAccountSlug(useParams().accountSlug);
  const {
    logoUrl: brandLogoUrl,
    name: brandName,
    slug: brandSlug,
    authProvider,
    loading: brandingLoading,
  } = useBranding(accountSlug);
  // Which backend endpoint this page authenticates against: on an account login page the
  // account decides (Pixo when enabled for it), otherwise the URL (see LOGIN_ROUTE_MAPPINGS)
  const loginRoute = useMemo(() => {
    if (accountSlug) {
      return authProvider === 'pixo' ? PIXO_LOGIN_ROUTE : DEFAULT_LOGIN_ROUTE;
    }
    return resolveLoginRoute();
  }, [accountSlug, authProvider]);
  const forgotPasswordPath = accountSlug
    ? `/forgot-password?account=${encodeURIComponent(accountSlug)}`
    : '/forgot-password';

  const displayLogo = brandLogoUrl || logo;
  const displayLogoAlt = brandName ? `${brandName} Logo` : 'ServiceHub Logo';

  const validateForm = () => {
    const errors = {};
    if (!email.trim()) {
      errors.email = 'Email or username is required';
    } else {
      delete errors.email;
    }
    if (!password) {
      errors.password = 'Password is required';
    } else {
      delete errors.password;
    }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleBlur = (field, value) => {
    if (!value || !String(value).trim()) {
      setFieldErrors(prev => ({ ...prev, [field]: field === 'email' ? 'Email or username is required' : `${field.charAt(0).toUpperCase() + field.slice(1)} is required` }));
    } else {
      setFieldErrors(prev => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) { return; }

    setLoading(true);
 
    try {
      posthog.capture('signin_clicked', { button_name: 'signin' })
      await login(email.trim(), password, undefined, accountSlug ? undefined : brandSlug, loginRoute, accountSlug);
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Invalid email/username or password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col relative overflow-hidden font-sans bg-base-200">
      {/* Background Mesh Gradients - Light Mode */}
      <div className="fixed inset-0 z-0 opacity-60">
        <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] bg-primary/10 blur-[120px] rounded-full" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[50%] h-[50%] bg-primary/5 blur-[120px] rounded-full" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[60%] h-[60%] bg-secondary/5 blur-[150px] rounded-full" />
      </div>

      <div className="relative z-10 flex-1 flex items-center justify-center p-6 bg-transparent">
        <div className="w-full max-w-md flex flex-col items-center">
          {/* Brand Header */}
          <div className="text-center mb-8 animate-fade-in-down">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-white rounded-2xl mb-4 shadow-xl border border-base-300 p-3 transition-transform duration-500 hover:scale-105">
              <img
                src={displayLogo}
                alt={displayLogoAlt}
                className="w-full h-full object-contain"
                onError={(e) => { e.currentTarget.src = logo; e.currentTarget.onerror = null; }}
              />
            </div>
            <h1 className="text-4xl font-black text-base-content mb-2 tracking-tighter leading-tight">
              {brandName || 'ServiceHub'}
            </h1>
            <p className="text-base-content/40 text-[10px] font-black tracking-[0.2em] uppercase">
              Service Manager Portal
            </p>
          </div>

          {/* Login Card */}
          <div className="w-full bg-base-100/80 backdrop-blur-2xl border border-base-300 rounded-3xl p-8 lg:p-10 shadow-[0_40px_80px_rgba(0,0,0,0.08)] relative group transition-all duration-500 hover:shadow-[0_50px_100px_rgba(0,0,0,0.12)]">
            <div className="absolute inset-0 bg-gradient-to-br from-white/40 to-transparent rounded-3xl pointer-events-none" />

            <h2 className="text-xl font-black text-base-content mb-8 tracking-tight uppercase">Sign in to workspace</h2>

            <form onSubmit={handleSubmit} className="space-y-6 relative z-10">
              <div className="space-y-2">
                <label htmlFor="email" className="block text-[10px] font-black text-base-content/40 uppercase tracking-widest pl-1">
                  Email or Username *
                </label>
                <input
                  id="email"
                  type="text"
                  autoComplete="username"
                  value={email}
                  onBlur={(e) => handleBlur('email', e.target.value)}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (fieldErrors.email) setFieldErrors(prev => {
                      const next = { ...prev };
                      delete next.email;
                      return next;
                    });
                  }}
                  className={`w-full bg-base-100 border rounded-xl px-4 py-3.5 text-base-content text-sm placeholder-base-content/30 outline-none transition-all duration-300 focus:border-primary focus:ring-4 focus:ring-primary/10 ${fieldErrors.email ? 'border-error ring-4 ring-error/10' : 'border-base-300'}`}
                  placeholder="your@email.com or username"
                />
                {fieldErrors.email && <p className="text-error text-xs mt-1 font-medium">{fieldErrors.email}</p>}
              </div>

              <div className="space-y-2">
                <div className="flex justify-between items-center pl-1 pr-1">
                  <label htmlFor="password" className="block text-[10px] font-black text-base-content/40 uppercase tracking-widest">
                    Secure Password *
                  </label>
                  {loginRoute.showForgotPassword && (
                    <Link
                      to={forgotPasswordPath}
                      className="text-[10px] font-bold text-primary hover:underline uppercase tracking-wider"
                    >
                      Forgot Password?
                    </Link>
                  )}
                </div>
                <input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onBlur={(e) => handleBlur('password', e.target.value)}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (fieldErrors.password) setFieldErrors(prev => {
                      const next = { ...prev };
                      delete next.password;
                      return next;
                    });
                  }}
                  className={`w-full bg-base-100 border rounded-xl px-4 py-3.5 text-base-content text-sm placeholder-base-content/30 outline-none transition-all duration-300 focus:border-primary focus:ring-4 focus:ring-primary/10 ${fieldErrors.password ? 'border-error ring-4 ring-error/10' : 'border-base-300'}`}
                  placeholder="••••••••"
                />
                {fieldErrors.password && <p className="text-error text-xs mt-1 font-medium">{fieldErrors.password}</p>}
              </div>

              {error && (
                <div className="bg-error/10 border border-error/20 text-error px-4 py-3 rounded-xl text-xs mt-1 font-medium animate-shake">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading || brandingLoading}
                className="w-full btn-premium py-4 font-black tracking-widest uppercase text-sm"
              >
                {loading ? 'Authenticating...' : 'Sign In'}
              </button>
            </form>
          </div>

          <p className="mt-10 text-base-content/20 text-[10px] font-black uppercase tracking-[0.25em]">
            &copy; {new Date().getFullYear()} SERVICEHUB • ENTERPRISE SECURE
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
